import { DestroyRef, Directive, ElementRef, inject, input, NgZone, signal } from '@angular/core';

@Directive({
	selector: '[appObserveWidth]',
	exportAs: 'appObserveWidth',
	standalone: true,
})
export class ObserveWidthDirective {
	readonly width = signal(0);
	readonly widthDebounced = signal(0);
	readonly debounceMs = input<number>(2000);

	private readonly el = inject(ElementRef<HTMLElement>);
	private readonly ngZone = inject(NgZone);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		let raf = 0;
		let timeoutId: number | null = null;
		let firstCommit = true;

		this.ngZone.runOutsideAngular(() => {
			const ro = new ResizeObserver((entries) => {
				if (raf) {
					cancelAnimationFrame(raf);
				}

				raf = requestAnimationFrame(() => {
					const w = Math.round(entries[0].contentRect.width);
					if (w === this.width()) {
						return;
					}

					this.width.set(w);

					if (firstCommit) {
						firstCommit = false;
						this.widthDebounced.set(w);
						return;
					}

					if (timeoutId) {
						clearTimeout(timeoutId);
					}

					timeoutId = window.setTimeout(() => {
						const current = this.width();
						if (current !== this.widthDebounced()) {
							this.widthDebounced.set(current);
						}
					}, this.debounceMs());
				});
			});

			ro.observe(this.el.nativeElement);

			this.destroyRef.onDestroy(() => {
				ro.disconnect();

				if (raf) {
					cancelAnimationFrame(raf);
				}

				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			});
		});
	}
}
